import React, { useState, useEffect } from 'react';
import { Button } from '@os/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@os/components/ui/card';
import { RotateCcw, History } from 'lucide-react';
import { useToast } from '@os/hooks/use-toast';

export const MapleCalc: React.FC = () => {
  const [display, setDisplay] = useState('0');
  const [previousValue, setPreviousValue] = useState<number | null>(null);
  const [operation, setOperation] = useState<string | null>(null);
  const [waitingForOperand, setWaitingForOperand] = useState(false);
  const [history, setHistory] = useState<string[]>([]);
  const [memory, setMemory] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const { toast } = useToast();

  // Debug effect to track display changes
  useEffect(() => {
    console.log('MapleCalc display changed to:', display);
  }, [display]);

  const inputNumber = (num: string) => {
    console.log('inputNumber called with:', num, 'current display:', display, 'waitingForOperand:', waitingForOperand);
    if (waitingForOperand) {
      setDisplay(num);
      setWaitingForOperand(false);
    } else {
      setDisplay(display === '0' ? num : display + num);
    }
  };

  const inputDecimal = () => {
    if (waitingForOperand) {
      setDisplay('0.');
      setWaitingForOperand(false);
    } else if (display.indexOf('.') === -1) {
      setDisplay(display + '.');
    }
  };

  const clear = () => {
    setDisplay('0');
    setPreviousValue(null);
    setOperation(null);
    setWaitingForOperand(false);
  };

  const performOperation = (nextOperation: string) => {
    const inputValue = parseFloat(display);

    if (previousValue === null) {
      setPreviousValue(inputValue);
    } else if (operation) {
      const currentValue = previousValue || 0;
      const result = calculate(currentValue, inputValue, operation);

      setDisplay(String(result));
      setPreviousValue(result);
      
      // Add to history
      const historyEntry = `${currentValue} ${operation} ${inputValue} = ${result}`;
      setHistory(prev => [historyEntry, ...prev.slice(0, 9)]);
    }

    setWaitingForOperand(true);
    setOperation(nextOperation);
  };

  const calculate = (firstValue: number, secondValue: number, operation: string): number => {
    switch (operation) {
      case '+':
        return firstValue + secondValue;
      case '-':
        return firstValue - secondValue;
      case '×':
        return firstValue * secondValue;
      case '÷':
        return secondValue !== 0 ? firstValue / secondValue : Infinity;
      case '^':
        return Math.pow(firstValue, secondValue);
      case '%':
        return firstValue % secondValue;
      case '=':
        return secondValue;
      default:
        return secondValue;
    }
  };

  const calculateScientific = (func: string) => {
    const value = parseFloat(display);
    let result: number;

    try {
      switch (func) {
        case 'sin':
          result = Math.sin(value * Math.PI / 180);
          break;
        case 'cos':
          result = Math.cos(value * Math.PI / 180);
          break;
        case 'tan':
          result = Math.tan(value * Math.PI / 180);
          break;
        case 'sqrt':
          result = value >= 0 ? Math.sqrt(value) : NaN;
          break;
        case 'square':
          result = value * value;
          break;
        case 'inverse':
          result = value !== 0 ? 1 / value : Infinity;
          break;
        case 'pi':
          result = Math.PI;
          break;
        case 'e':
          result = Math.E;
          break;
        default:
          result = value;
      }

      if (isNaN(result) || !isFinite(result)) {
        setDisplay('Error');
        return;
      }

      const formattedResult = parseFloat(result.toPrecision(12));
      setDisplay(String(formattedResult));
      setWaitingForOperand(true);
      
      const historyEntry = `${func}(${value}) = ${formattedResult}`;
      setHistory(prev => [historyEntry, ...prev.slice(0, 9)]);
    } catch (error) {
      setDisplay('Error');
    }
  };

  const memoryStore = () => {
    setMemory(parseFloat(display));
    toast({
      title: "Memory Stored",
      description: `Value ${display} stored in memory.`,
    });
  };

  const memoryRecall = () => {
    setDisplay(String(memory));
    setWaitingForOperand(true);
  };

  const memoryClear = () => {
    setMemory(0);
    toast({
      title: "Memory Cleared",
      description: "Memory has been cleared.",
    });
  };

  const clearHistory = () => {
    setHistory([]);
    toast({
      title: "History Cleared",
      description: "Calculation history has been cleared.",
    });
  };

  return (
    <div className="flex h-full bg-background">
      {/* Main Calculator */}
      <div className="flex-1 p-4">
        <Card className="h-full">
          <CardHeader className="pb-4">
            <div className="flex items-center justify-end gap-2">
              <Button
                onClick={() => setShowHistory(!showHistory)}
                size="sm"
                variant="outline"
              >
                <History className="w-4 h-4" />
              </Button>
              <Button onClick={clear} size="sm" variant="outline">
                <RotateCcw className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            {/* Display */}
            <div className="p-4 bg-muted rounded-lg">
              <div className="text-right text-2xl font-mono h-16 flex items-center justify-end px-3 bg-background rounded border min-h-[64px] overflow-hidden">
                <span className="text-foreground select-all break-all">
                  {display}
                </span>
              </div>
              {memory !== 0 && (
                <div className="text-xs text-muted-foreground mt-1">Memory: {memory}</div>
              )}
            </div>

            {/* Scientific Functions Row 1 */}
            <div className="grid grid-cols-6 gap-2">
              <button
                onClick={() => {
                  console.log('sin button clicked');
                  calculateScientific('sin');
                }}
                className="h-12 text-lg font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md"
              >
                sin
              </button>
              <button
                onClick={() => {
                  console.log('cos button clicked');
                  calculateScientific('cos');
                }}
                className="h-12 text-lg font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md"
              >
                cos
              </button>
              <button
                onClick={() => {
                  console.log('tan button clicked');
                  calculateScientific('tan');
                }}
                className="h-12 text-lg font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md"
              >
                tan
              </button>
              <button
                onClick={() => {
                  console.log('sqrt button clicked');
                  calculateScientific('sqrt');
                }}
                className="h-12 text-lg font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md"
              >
                √
              </button>
              <button
                onClick={() => {
                  console.log('pi button clicked');
                  calculateScientific('pi');
                }}
                className="h-12 text-lg font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md"
              >
                π
              </button>
              <button
                onClick={() => {
                  console.log('clear button clicked');
                  clear();
                }}
                className="h-12 text-lg font-medium bg-red-500 text-white hover:bg-red-600 rounded-md"
              >
                C
              </button>
            </div>

            {/* Scientific Functions Row 2 */}
            <div className="grid grid-cols-6 gap-2">
              <button
                onClick={() => {
                  console.log('square button clicked');
                  calculateScientific('square');
                }}
                className="h-12 text-lg font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md"
              >
                x²
              </button>
              <button
                onClick={() => {
                  console.log('inverse button clicked');
                  calculateScientific('inverse');
                }}
                className="h-12 text-lg font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md"
              >
                1/x
              </button>
              <button
                onClick={() => {
                  console.log('e button clicked');
                  calculateScientific('e');
                }}
                className="h-12 text-lg font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md"
              >
                e
              </button>
              <button
                onClick={() => {
                  console.log('MS button clicked');
                  memoryStore();
                }}
                className="h-12 text-lg font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md"
              >
                MS
              </button>
              <button
                onClick={() => {
                  console.log('MR button clicked');
                  memoryRecall();
                }}
                className="h-12 text-lg font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md"
              >
                MR
              </button>
              <button
                onClick={() => {
                  console.log('MC button clicked');
                  memoryClear();
                }}
                className="h-12 text-lg font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md"
              >
                MC
              </button>
            </div>

            {/* Number Pad */}
            <div className="grid grid-cols-4 gap-2">
              <button
                onClick={() => {
                  console.log('7 button clicked');
                  inputNumber('7');
                }}
                className="h-12 text-lg font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md"
              >
                7
              </button>
              <button
                onClick={() => {
                  console.log('8 button clicked');
                  inputNumber('8');
                }}
                className="h-12 text-lg font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md"
              >
                8
              </button>
              <button
                onClick={() => {
                  console.log('9 button clicked');
                  inputNumber('9');
                }}
                className="h-12 text-lg font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md"
              >
                9
              </button>
              <button
                onClick={() => {
                  console.log('÷ button clicked');
                  performOperation('÷');
                }}
                className="h-12 text-lg font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md"
              >
                ÷
              </button>
            </div>

            <div className="grid grid-cols-4 gap-2">
              <button
                onClick={() => {
                  console.log('4 button clicked');
                  inputNumber('4');
                }}
                className="h-12 text-lg font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md"
              >
                4
              </button>
              <button
                onClick={() => {
                  console.log('5 button clicked');
                  inputNumber('5');
                }}
                className="h-12 text-lg font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md"
              >
                5
              </button>
              <button
                onClick={() => {
                  console.log('6 button clicked');
                  inputNumber('6');
                }}
                className="h-12 text-lg font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md"
              >
                6
              </button>
              <button
                onClick={() => {
                  console.log('× button clicked');
                  performOperation('×');
                }}
                className="h-12 text-lg font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md"
              >
                ×
              </button>
            </div>

            <div className="grid grid-cols-4 gap-2">
              <button
                onClick={() => {
                  console.log('1 button clicked');
                  inputNumber('1');
                }}
                className="h-12 text-lg font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md"
              >
                1
              </button>
              <button
                onClick={() => {
                  console.log('2 button clicked');
                  inputNumber('2');
                }}
                className="h-12 text-lg font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md"
              >
                2
              </button>
              <button
                onClick={() => {
                  console.log('3 button clicked');
                  inputNumber('3');
                }}
                className="h-12 text-lg font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md"
              >
                3
              </button>
              <button
                onClick={() => {
                  console.log('- button clicked');
                  performOperation('-');
                }}
                className="h-12 text-lg font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md"
              >
                -
              </button>
            </div>

            <div className="grid grid-cols-4 gap-2">
              <button
                onClick={() => {
                  console.log('0 button clicked');
                  inputNumber('0');
                }}
                className="col-span-2 h-12 text-lg font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md"
              >
                0
              </button>
              <button
                onClick={() => {
                  console.log('. button clicked');
                  inputDecimal();
                }}
                className="h-12 text-lg font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md"
              >
                .
              </button>
              <button
                onClick={() => {
                  console.log('+ button clicked');
                  performOperation('+');
                }}
                className="h-12 text-lg font-medium border border-input bg-background hover:bg-accent hover:text-accent-foreground rounded-md"
              >
                +
              </button>
            </div>

            <div className="grid grid-cols-4 gap-2">
              <button
                onClick={() => {
                  console.log('= button clicked');
                  performOperation('=');
                }}
                className="col-span-4 h-12 text-lg font-medium bg-blue-500 text-white hover:bg-blue-600 rounded-md"
              >
                =
              </button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* History Panel */}
      {showHistory && (
        <div className="w-80 border-l border-border p-4">
          <Card className="h-full">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">History</CardTitle>
                <Button onClick={clearHistory} size="sm" variant="outline">
                  Clear
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {history.length === 0 ? (
                  <p className="text-muted-foreground text-sm">No calculations yet</p>
                ) : (
                  history.map((entry, index) => (
                    <div
                      key={index}
                      className="p-2 bg-muted rounded text-sm font-mono cursor-pointer hover:bg-muted/80"
                      onClick={() => {
                        const result = entry.split(' = ')[1];
                        if (result) {
                          setDisplay(result);
                          setWaitingForOperand(true);
                        }
                      }}
                    >
                      {entry}
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};